import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Sparkle, ShieldCheck } from "@phosphor-icons/react";

/**
 * Save a partner-approved edit as a house-voice few-shot example.
 *
 * Governance rules surfaced to the partner in this dialog:
 *   • Learning is opt-in — this dialog only opens when the partner clicks
 *     "Save to house voice" AFTER filing (never automatic).
 *   • Firm-wide guidance is redacted server-side; the partner is told so.
 *   • Engagement-specific examples never leave the governance UI — they don't
 *     feed AI drafters (backend enforces this).
 *   • The partner must confirm the excerpt contains no confidential material.
 */
export default function SaveToHouseVoiceDialog({
  open,
  onOpenChange,
  sourceKind = "closeout",
  originalText = "",
  approvedText = "",
  engagementId,
  clientId,
  engagementType,
  onSaved,
}) {
  const [orig, setOrig] = useState(originalText);
  const [appr, setAppr] = useState(approvedText);
  const [scope, setScope] = useState("firm_wide");
  const [note, setNote] = useState("");
  const [tags, setTags] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);

  // Reset the local copies whenever the dialog re-opens with a new draft.
  const handleOpenChange = (v) => {
    if (v) {
      setOrig(originalText);
      setAppr(approvedText);
      setScope("firm_wide");
      setNote("");
      setTags("");
      setConfirmed(false);
    }
    onOpenChange(v);
  };

  const identical = orig.trim() === appr.trim();
  const canSave = confirmed && !identical && orig.trim() && appr.trim();

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      await api.post("/voice/examples", {
        source_kind: sourceKind,
        scope,
        original_text: orig,
        approved_text: appr,
        engagement_id: scope === "engagement_specific" ? engagementId : engagementId || null,
        client_id: clientId || null,
        engagement_type: engagementType || null,
        tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
        note: note.trim() || null,
        is_confidential: true,
      });
      toast.success(scope === "firm_wide"
        ? "Saved to firm-wide voice library (client names auto-redacted)"
        : "Saved as engagement-specific note (not used by AI drafters)");
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="rounded-none bg-card max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="save-house-voice-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Sparkle size={16} weight="fill" className="text-primary" />
            Save to house voice
          </DialogTitle>
        </DialogHeader>

        <div className="text-xs italic text-muted-foreground" style={{ fontFamily: "Georgia, serif" }}>
          One approved edit at a time. YadidAi may reference these as advisory context — never to alter canonical firm language.
        </div>

        <div className="space-y-4">
          <div>
            <Label className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">Scope</Label>
            <RadioGroup value={scope} onValueChange={setScope} className="mt-2 space-y-2" data-testid="voice-scope">
              <label className="flex items-start gap-2 cursor-pointer">
                <RadioGroupItem value="firm_wide" id="scope-firm" data-testid="scope-firm-radio" />
                <div className="text-sm">
                  <div className="font-medium">Firm-wide voice guidance</div>
                  <div className="text-xs text-muted-foreground">Feeds YadidAi's house-voice context. Partner organization names, emails, and phone numbers are auto-redacted before storage.</div>
                </div>
              </label>
              <label className="flex items-start gap-2 cursor-pointer">
                <RadioGroupItem value="engagement_specific" id="scope-eng" data-testid="scope-eng-radio" />
                <div className="text-sm">
                  <div className="font-medium">Engagement-specific note</div>
                  <div className="text-xs text-muted-foreground">Kept as a reference in the governance UI only. Never fed to AI drafters.</div>
                </div>
              </label>
            </RadioGroup>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">Original draft</Label>
              <Textarea rows={6} value={orig} onChange={(e) => setOrig(e.target.value)}
                        className="rounded-none bg-secondary/40 mt-1 text-sm" data-testid="voice-original" />
            </div>
            <div>
              <Label className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">Approved edit</Label>
              <Textarea rows={6} value={appr} onChange={(e) => setAppr(e.target.value)}
                        className="rounded-none bg-secondary/40 mt-1 text-sm" data-testid="voice-approved" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">Tags (comma separated)</Label>
              <Input value={tags} onChange={(e) => setTags(e.target.value)}
                     placeholder="tone, brevity, closeout" className="rounded-none bg-secondary/40 mt-1" data-testid="voice-tags" />
            </div>
            <div>
              <Label className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">Why this matters (optional)</Label>
              <Input value={note} onChange={(e) => setNote(e.target.value)}
                     placeholder="e.g. cut hedging language, kept partner cadence" className="rounded-none bg-secondary/40 mt-1" data-testid="voice-note" />
            </div>
          </div>

          <label className="flex items-start gap-2 text-xs cursor-pointer border border-primary/30 p-3 bg-secondary/20">
            <Checkbox checked={confirmed} onCheckedChange={setConfirmed} data-testid="voice-confirm" />
            <div>
              <div className="flex items-center gap-1.5"><ShieldCheck size={14} className="text-primary" /> I confirm no privileged, confidential, or client-attributable content remains in the "Approved edit" text above.</div>
              {scope === "firm_wide" && (
                <div className="text-muted-foreground mt-1">Server will additionally scrub proper-noun names, emails, and phone-shaped tokens before the example is exposed to AI drafters.</div>
              )}
            </div>
          </label>

          {identical && (
            <div className="text-xs text-amber-500/80">Original and approved texts are identical — nothing to learn from.</div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-none">Cancel</Button>
          <Button onClick={save} disabled={!canSave || saving}
                  className="rounded-none bg-primary text-primary-foreground disabled:opacity-50"
                  data-testid="voice-save-btn">
            {saving ? "Saving…" : "Save example"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
